import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Clock, DollarSign, User, Calendar, MessageSquare } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import payoutService from '../../services/payoutService';
import toast from 'react-hot-toast';

const PayoutsContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 10px 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  color: #b8b8d1;
  font-size: 16px;
  margin: 0;
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 5px;
`;

const FilterTab = styled.button`
  background: ${props => props.active ? 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)' : 'transparent'};
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  color: ${props => props.active ? 'white' : '#b8b8d1'};
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)' : 'rgba(0, 212, 255, 0.1)'};
    color: ${props => props.active ? 'white' : '#00d4ff'};
  }
`;

const PayoutsTable = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
  gap: 20px;
  padding: 20px;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
  border-bottom: 1px solid #2d2d44;
  font-weight: 600;
  color: #ffffff;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
  gap: 20px;
  padding: 20px;
  border-bottom: 1px solid #2d2d44;
  align-items: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(90deg, rgba(0, 212, 255, 0.05) 0%, transparent 100%);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 16px;
`;

const UserDetails = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  color: #ffffff;
  font-weight: 600;
  margin-bottom: 5px;
`;

const UserEmail = styled.div`
  color: #6c6c8a;
  font-size: 14px;
`;

const Amount = styled.div`
  color: #4ecdc4;
  font-weight: 600;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StatusBadge = styled.div`
  display: inline-block;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'approved': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      case 'pending': return 'linear-gradient(135deg, #ffd93d 0%, #ff6b6b 100%)';
      case 'rejected': return 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)';
      case 'processed': return 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)';
      default: return 'linear-gradient(135deg, #6c6c8a 0%, #4a4a6a 100%)';
    }
  }};
  color: white;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  background: ${props => {
    switch(props.variant) {
      case 'approve': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      case 'reject': return 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)';
      case 'process': return 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)';
      default: return 'transparent';
    }
  }};
  border: 1px solid ${props => {
    switch(props.variant) {
      case 'approve': return '#4ecdc4';
      case 'reject': return '#ff6b6b';
      case 'process': return '#00d4ff';
      default: return '#2d2d44';
    }
  }};
  border-radius: 6px;
  padding: 8px 12px;
  color: ${props => {
    switch(props.variant) {
      case 'approve':
      case 'reject':
      case 'process': return 'white';
      default: return '#b8b8d1';
    }
  }};
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c6c8a;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #b8b8d1;
  font-size: 20px;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  color: #6c6c8a;
  font-size: 16px;
  margin: 0;
`;

const Payouts = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await payoutService.getPendingWithdrawals();
      setRequests(response || []);
    } catch (error) {
      toast.error('خطا در بارگذاری درخواست‌های پرداخت');
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = requests.filter(request => {
    if (filter === 'all') return true;
    return request.status === filter;
  });

  const handleApprove = async (requestId) => {
    try {
      setActionLoading(requestId);
      await payoutService.approveWithdrawal(requestId);
      setRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, status: 'approved' }
            : req
        )
      );
      toast.success('درخواست تایید شد');
    } catch (error) {
      toast.error('خطا در تایید درخواست');
      console.error('Error approving request:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (requestId) => {
    if (window.confirm('آیا از رد این درخواست اطمینان دارید؟')) {
      try {
        setActionLoading(requestId);
        await payoutService.rejectWithdrawal(requestId, 'رد شده توسط ادمین');
        setRequests(prev => 
          prev.map(req => 
            req.id === requestId 
              ? { ...req, status: 'rejected' }
              : req
          )
        );
        toast.success('درخواست رد شد');
      } catch (error) {
        toast.error('خطا در رد درخواست');
        console.error('Error rejecting request:', error);
      } finally {
        setActionLoading(null);
      }
    }
  };

  const handleProcess = async (requestId) => {
    try {
      setActionLoading(requestId);
      await payoutService.processPayout(requestId);
      setRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, status: 'processed' }
            : req
        )
      );
      toast.success('پرداخت انجام شد');
    } catch (error) {
      toast.error('خطا در انجام پرداخت');
      console.error('Error processing payout:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'approved': return 'تایید شده';
      case 'pending': return 'در انتظار';
      case 'rejected': return 'رد شده';
      case 'processed': return 'پرداخت شده';
      default: return 'نامشخص';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fa-IR', {
      style: 'currency',
      currency: 'IRR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR');
  };

  if (loading) {
    return (
      <PayoutsContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ color: '#b8b8d1' }}>در حال بارگذاری درخواست‌های پرداخت...</div>
        </div>
      </PayoutsContainer>
    );
  }

  return (
    <PayoutsContainer>
      <Header>
        <Title>مدیریت پرداخت‌ها</Title>
        <Subtitle>درخواست‌های برداشت و پرداخت بازیکنان</Subtitle>
      </Header>

      <FilterTabs>
        <FilterTab 
          active={filter === 'all'} 
          onClick={() => setFilter('all')}
        >
          همه درخواست‌ها
        </FilterTab>
        <FilterTab 
          active={filter === 'pending'} 
          onClick={() => setFilter('pending')}
        >
          در انتظار
        </FilterTab>
        <FilterTab 
          active={filter === 'approved'} 
          onClick={() => setFilter('approved')}
        >
          تایید شده
        </FilterTab>
        <FilterTab 
          active={filter === 'processed'} 
          onClick={() => setFilter('processed')}
        >
          پرداخت شده
        </FilterTab>
        <FilterTab 
          active={filter === 'rejected'} 
          onClick={() => setFilter('rejected')}
        >
          رد شده
        </FilterTab>
      </FilterTabs>

      {filteredRequests.length === 0 ? (
        <EmptyState>
          <EmptyIcon>💳</EmptyIcon>
          <EmptyTitle>هیچ درخواستی یافت نشد</EmptyTitle>
          <EmptyDescription>
            {filter === 'all' ? 'هنوز درخواست پرداختی وجود ندارد' : 'درخواستی با این وضعیت یافت نشد'}
          </EmptyDescription>
        </EmptyState>
      ) : (
        <PayoutsTable>
          <TableHeader>
            <div>بازیکن</div>
            <div>مبلغ</div>
            <div>وضعیت</div>
            <div>تاریخ درخواست</div>
            <div>روش پرداخت</div>
            <div>عملیات</div>
          </TableHeader>
          
          {filteredRequests.map((request, index) => (
            <TableRow
              key={request.id || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <UserInfo>
                <UserAvatar>
                  {request.userName?.charAt(0)?.toUpperCase() || 'U'}
                </UserAvatar>
                <UserDetails>
                  <UserName>{request.userName || 'بازیکن'}</UserName>
                  <UserEmail>{request.userEmail || 'ایمیل ندارد'}</UserEmail>
                </UserDetails>
              </UserInfo>
              
              <Amount>
                <DollarSign size={16} />
                {formatCurrency(request.amount)}
              </Amount>
              
              <StatusBadge status={request.status}>
                {getStatusText(request.status)}
              </StatusBadge>
              
              <div style={{ color: '#b8b8d1', fontSize: '14px' }}>
                {formatDate(request.requestDate)}
              </div>
              
              <div style={{ color: '#b8b8d1', fontSize: '14px' }}>
                {request.paymentMethod || 'نامشخص'}
              </div>
              
              <Actions>
                {request.status === 'pending' && (
                  <>
                    <ActionButton
                      variant="approve"
                      onClick={() => handleApprove(request.id)}
                      disabled={actionLoading === request.id}
                    >
                      <CheckCircle size={14} />
                      تایید
                    </ActionButton>
                    <ActionButton
                      variant="reject"
                      onClick={() => handleReject(request.id)}
                      disabled={actionLoading === request.id}
                    >
                      <XCircle size={14} />
                      رد
                    </ActionButton>
                  </>
                )}
                {request.status === 'approved' && (
                  <ActionButton
                    variant="process"
                    onClick={() => handleProcess(request.id)}
                    disabled={actionLoading === request.id}
                  >
                    <DollarSign size={14} />
                    پرداخت
                  </ActionButton>
                )}
              </Actions>
            </TableRow>
          ))}
        </PayoutsTable>
      )}
    </PayoutsContainer>
  );
};

export default Payouts;
